import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
// import API from '../../../services/api'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { ButtonCss } from '../PaymentModal.styled'
import { ScratchCardImage } from '@Components/Layout/Header/Assets/images'
import { usePaymentCheckout } from '../usePaymentDispatch'
// import { refreshWalletValue } from './Utils'
import { trackEvent } from '@Utilities/marketingTags'
import type { PaymentModalVariant } from '../PaymentModal'

/** أبواب scratch cards are exactly 10 characters. */
const CODE_LENGTH = 10
/** Cards are read off print, so codes arrive with spaces or dashes in them. */
const sanitizeCode = (raw: string) => raw.replace(/[^a-zA-Z0-9]/g, '').slice(0, CODE_LENGTH)

type Props = {
	courseId: any
	courseName: any
	setShowPaymentPopup: any
	onSuccess: any
	onReject: any
	price: any
	variant?: PaymentModalVariant
}
const ScratchcardPayment: React.FC<Props> = ({ courseId, courseName, setShowPaymentPopup, onSuccess, onReject, price, variant = 'course' }) => {
	const dispatch = useDispatch()
	const { t } = useTranslation(['payment'], { keyPrefix: 'scratch_card_payment' })

	// One field, not ten boxes: the ten single-character inputs could not take a
	// pasted code (the paste landed entirely in one box), which is how students
	// actually enter a code they received as text.
	const [code, setCode] = useState('')
	const isValid = code.length === CODE_LENGTH
	// The variant → endpoint/id-field mapping lives in one shared hook.
	const { checkout, state: checkoutData } = usePaymentCheckout(variant, courseId)

	const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCode(sanitizeCode(event.target.value))
	}

	const submitHandler = async () => {
		if (!isValid) return
		try {
			checkout({ payment_method: 'scratchCard', code })
		} catch (e) {
			if (onReject) onReject()
			setCode('')
		}
	}
	useEffect(() => {
		if (checkoutData.isSuccess) {
			// Scratch redemptions confirm synchronously — this is the purchase
			// moment. (Old event name: 'Purchases'.)
			trackEvent('Purchase', {
				content_id: courseId,
				content_name: courseName,
				content_type: variant,
				payment_method: 'scratchCard',
				currency: 'EGP',
				value: Number(price),
			})
			onSuccess && onSuccess()
		}
	}, [checkoutData.isSuccess, onSuccess])
	useEffect(() => {
		if (checkoutData.isError) {
			trackEvent('PurchaseFailed', {
				content_id: courseId,
				content_type: variant,
				payment_method: 'scratchCard',
				currency: 'EGP',
				value: Number(price),
			})
			onReject && onReject()
		}
	}, [checkoutData.isError, onReject])
	// Bundle scratch cards must EQUAL the bundle price, and that rejection is
	// easy to misread from a transient snackbar — so keep it readable in place.
	// Scoped to the bundle variant: the classic course / standalone flows kept
	// their existing snackbar-only rejection UX (via onReject) before this.
	const errorMessage =
		variant === 'bundle' && checkoutData.isError
			? (checkoutData.error as { data?: { message?: string } } | undefined)?.data?.message ??
			  t('rejected', { defaultValue: 'تعذّر استخدام هذا الكارت — تأكد من الكود وقيمته.' })
			: undefined
	return (
		<StyledScratchCardPayment>
			<Header>{t('title')}</Header>
			<ContentWrapper>
				<Image src={ScratchCardImage} alt="scratch card image" />

				<InputContainer>
					<InputSection>
						<CodeInput
							onChange={onChangeHandler}
							value={code}
							name="scratch-code"
							inputMode="text"
							autoComplete="off"
							autoCapitalize="characters"
							spellCheck={false}
							maxLength={CODE_LENGTH}
							aria-label={t('title')}
							placeholder={t('code_placeholder', { defaultValue: 'اكتب أو الصق كود الكارت' })}
							onKeyDown={event => {
								if (event.key === 'Enter' && isValid) submitHandler()
							}}
						/>
					</InputSection>
					{errorMessage && <ErrorNote>{errorMessage}</ErrorNote>}
					<SendButton disabled={!isValid} onClick={submitHandler}>
						{t('confirm')}
					</SendButton>
				</InputContainer>
			</ContentWrapper>
		</StyledScratchCardPayment>
	)
}

const StyledScratchCardPayment = styled.div`
	height: 100%;

	@media (max-width: 425px) {
		min-height: 200px;
	}
`

const ContentWrapper = styled.div`
	display: flex;
	align-items: center;
	height: 100%;
	padding-top: 26px;

	@media (max-width: 425px) {
		flex-direction: column;
	}
`

const Image = styled.img`
	max-width: 177px;
	margin-top: -56px;

	@media (max-width: 425px) {
		margin-top: 0;
		display: none;
	}
`

const InputContainer = styled.div`
	/* width: 100%; */
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	direction: ltr;

	@media (max-width: 425px) {
		top: 80px;
		/* position: absolute; */
	}
`

const InputSection = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
`

/* A single field: one selection, one paste, one clear. */
const CodeInput = styled.input`
	width: 100%;
	min-width: 220px;
	height: 46px;
	padding: 0 14px;
	border-radius: 8px;
	border: 1px solid #c4c4c4;
	background: var(--surface-card, #fff);
	font-family: var(--font-sans);
	font-size: 18px;
	/* codes are Latin/among digits — keep them LTR inside the RTL modal */
	direction: ltr;
	text-align: center;
	letter-spacing: 2px;
	color: var(--text-primary, #0f172a);
	&::placeholder {
		font-size: 13px;
		letter-spacing: normal;
		color: var(--text-disabled, #94a3b8);
		direction: rtl;
	}
	&:focus {
		outline: none;
		border-color: var(--blue, #0655cb);
	}
	${({ theme }) => `${theme.mediaQueries.large}{
		height: 50px;
		font-size: 20px;
	}`}
`

const ErrorNote = styled.p`
	font: var(--fw-bold, 700) 12px var(--font-sans);
	color: var(--error-dark, #b91c1c);
	margin-top: 12px;
	text-align: center;
	direction: rtl;
`

const SendButton = styled.button`
	${ButtonCss};
	//background-color: #0655CB;
	background-color: ${props => (props.disabled ? 'gray' : '#0655CB')};
	margin-top: 34px;

	&:hover {
		cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
	}
`

export default ScratchcardPayment

const Header = styled.h3`
	font-family: var(--font-sans);
	font-size: 18px;
	font-weight: 600;
	text-align: center;
`
